import apiClient from "config/apiClient";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import insertObjectIf, { toSnakeCase } from "util/helper";
import { getUserType } from "util/auth";
import { Quotation, QuotationList, QuotationRequest, QuoteDetail } from "../types";
import {
  QuotationQuery,
  SendSmsRequest,
  SendSmsResponse,
  TblQuoteQuoteBySelectResponse,
  TblQuoteSelectResponse
} from "../types/quotation";

/**
 * Fetches a list of quotations.
 * @param {QuotationRequest} params - The request parameters for fetching quotations.
 * @param {boolean} [withPdf] - Whether to include PDF data in the response.
 * @returns {Promise<Quotation[]>} - A promise resolving to an array of quotations.
 * @throws {Error} If the API call fails or returns a non-200 status.
 */
export async function quotationListApi(
  params: QuotationRequest,
  withPdf = false
): Promise<QuoteDetail[]> {
  const res = await apiClient.post<{ list: QuoteDetail[]; total: number }>("/tbl/quote/select", {
    ...params,
    ...(withPdf && { read_pdf: "yes" })
  });
  if (res.status !== 200) {
    throw new Error("Failed to fetch quotation list");
  }
  const { data } = res;
  if (data) {
    const { list, total } = data;
    if (total === 0) {
      return [];
    }
    return list;
  }
  return [];
}

/**
 * Fetches a list of quotations based on the provided query parameters.
 * @param {QuotationQuery} [body] - The query parameters for fetching quotations.
 * @returns {Promise<TblQuoteSelectResponse>} - A promise resolving to the list of quotations and total count.
 */
export async function getQuotationListApi(body?: QuotationQuery): Promise<TblQuoteSelectResponse> {
  const res = await apiClient.post<TblQuoteSelectResponse>(
    `/tbl/quote/select`,
    toSnakeCase({
      limit: body?.limit || DEFAULT_LIMIT_ITEMS,
      offset: body?.offset || 1,
      ...insertObjectIf(body?.quote_id, { quote_id: body?.quote_id }),
      ...insertObjectIf(body?.quote_no, { quote_no: body?.quote_no }),
      ...insertObjectIf(body?.customer_name, { customer_name: body?.customer_name }),
      ...insertObjectIf(body?.customer_tel_no, { customer_tel_no: body?.customer_tel_no }),
      ...insertObjectIf(body?.store_or_company, { store_or_company: body?.store_or_company }),
      ...insertObjectIf(body?.quote_by, { quote_by: body?.quote_by }),
      ...insertObjectIf(body?.created_at_start, { created_at_start: body?.created_at_start }),
      ...insertObjectIf(body?.created_at_end, { created_at_end: body?.created_at_end }),
      ...insertObjectIf(body?.expired_flg !== undefined, { expired_flg: body?.expired_flg }),
      ...insertObjectIf(body?.order_flag_flg !== undefined, {
        order_flag_flg: body?.order_flag_flg
      }),
      ...insertObjectIf(body?.delete_flag !== undefined, { delete_flag: body?.delete_flag })
    })
  );
  return { list: res.data?.list || [], total: res.data?.total || 0 };
}

/**
 * get quotation by select
 * 店舗ログイン（C0002）時は localStorage の shop_cd を store_cd として送信する。
 * @returns TblQuoteBy[]
 */
export async function getQuoteByListApi(): Promise<TblQuoteQuoteBySelectResponse> {
  const isStoreLogin = getUserType() === "C0002";
  const storeCd = localStorage.getItem("shop_cd") ?? "";
  const body = isStoreLogin && storeCd ? { store_cd: storeCd } : {};
  const res = await apiClient.post<TblQuoteQuoteBySelectResponse>(
    `/tbl/quote/quote/by/select`,
    body
  );
  return { list: res.data?.list || [], total: res.data?.total || 0 };
}

/**
 * Uploads a PDF and optionally sends SMS. Backend uploads the PDF to S3 and returns the public URL.
 * @param {SendSmsRequest} params - The request parameters including `sns_send_flag` (1: SMS送信, 0: PDF出力のみ).
 * @returns {Promise<SendSmsResponse>} - Resolves with the S3 URL of the uploaded PDF.
 */
export async function sendSmsApi(params: SendSmsRequest): Promise<SendSmsResponse> {
  // If PDF file exists, use FormData
  if (params.pdf_file) {
    const formData = new FormData();
    formData.append("quote_id", String(params.quote_id));
    formData.append("quote_no", params.quote_no);
    formData.append("phone_number", params.phone_number);
    formData.append("pdf_file", params.pdf_file);
    formData.append("sns_send_flag", String(params.sns_send_flag));

    const res = await apiClient.post<SendSmsResponse>(`/tbl/quote/send/sns`, formData, { "Content-Type": "multipart/form-data" });
    if (res.status !== 200) {
      throw new Error("Failed to send SMS");
    }
    return (res.data as SendSmsResponse) ?? { url: "" };
  }
  throw new Error("PDF file is required");
}
